import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Shield } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/insurance", label: "Solutions" },
  { to: "/careers", label: "Careers" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-9 h-9 border border-[#D4AF37]/60 flex items-center justify-center relative">
            <Shield className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <div className="absolute inset-0 border border-[#D4AF37]/20 scale-[1.4] group-hover:scale-100 transition-transform duration-500" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.32em] text-[#D4AF37] font-medium">IMPACT LEGACY</div>
            <div className="text-[10px] tracking-[0.32em] text-white/60 font-medium">GROUP</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs tracking-[0.22em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase transition-all duration-300"
        >
          Get Started
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-black/95 backdrop-blur-2xl border-t border-white/5">
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.22em] uppercase ${isActive ? "text-[#D4AF37]" : "text-white/80"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="self-start border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
